import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Translate } from 'react-localize-redux'
import Source from 'containers/ActivitiesContainer/components/Source'
import {
  fetchArticleProviders,
  fetchTeamArticles,
  updateFavoriteArticle,
} from 'routes/ArticlesDashboard/api'
import PropTypes from 'prop-types'
import { Box, Button, Grid, TablePagination } from '@material-ui/core'
import { LoadingSpinner, NotFound } from 'components'
import SearchBar from 'material-ui-search-bar'
import { debounce } from 'lodash'
import CategoriesDialog from 'containers/ActivitiesContainer/components/CategoriesDialog'
import { articles as articlesModule } from 'redux/modules'
import useStyles from '../styles'
import { fetchClubArticleCategories, fetchFavouriteArticles } from '../api'
import { MenuPaper } from './StyledComponents'
import ArticleCard from './ArticleCard'

const ArticlesContainer = ({ team, deleteArticle }) => {
  const classes = useStyles()

  const [articleProviders, setArticleProviders] = useState([])
  const [providers, setProviders] = useState([])
  const [selectedSource, setSelectedSource] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(12)
  const [page, setPage] = useState(0)
  const [articles, setArticles] = useState([])
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [articleCategories, setArticleCategories] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])

  const handleSourceChange = (event) => {
    setSelectedSource(event.target.value)
    setSelectedCategories([])
    setSearchQuery('')
    setPage(0)
    setRowsPerPage(12)
  }

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories)
    setPage(0)
    setRowsPerPage(12)
  }

  const handleReset = () => {
    setSelectedCategories([])
    setSearchQuery('')
    setPage(0)
    setRowsPerPage(12)
  }

  useEffect(() => {
    fetchArticleProviders(team.uuid)
      .then((res) => {
        setArticleProviders(res.data)
        setSelectedSource(res.data[0].uuid)
      })
      .catch((err) => {
        console.error('Error fetching article providers:', err)
      })
  }, [team])

  useEffect(() => {
    if (
      selectedSource &&
      selectedSource !== '' &&
      selectedSource !== 'my-team-articles' &&
      selectedSource !== 'my-favourites'
    ) {
      fetchClubArticleCategories(selectedSource, {
        include: 'children.children',
      })
        .then((res) => {
          setArticleCategories(res.data)
        })
        .catch((err) => {
          console.error('Error fetching categories:', err)
        })
    }
  }, [selectedSource])

  const fetchArticles = async (filters) => {
    try {
      let response = null
      const params = {
        ...filters,
        per_page: rowsPerPage,
        page: page + 1,
        ...(selectedSource !== 'my-favourites' && { include: 'image' }),
      }
      if (selectedSource === 'my-team-articles') {
        response = await fetchTeamArticles(team.uuid, params)
      } else if (selectedSource === 'my-favourites') {
        response = await fetchFavouriteArticles(team.uuid, params)
      } else {
        response = await fetchTeamArticles(team.uuid, {
          ...params,
          provider_club_uuid: selectedSource,
        })
      }
      setArticles(response.data)
      setMeta(response.meta)
    } catch (error) {
      console.error('Error fetching articles:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    const filters = {}
    if (selectedSource && selectedSource !== 'my-team-articles') {
      if (selectedCategories.length > 0) {
        filters['filter[categories]'] = selectedCategories.join(',')
      }
    }
    if (searchQuery) {
      filters['filter[search]'] = searchQuery
    }
    if (selectedSource) {
      fetchArticles(filters)
    }
  }, [selectedSource, selectedCategories, searchQuery, rowsPerPage, page])

  useEffect(() => {
    if (articleProviders.length > 0) {
      const validProviders = articleProviders.filter(
        (provider) => provider.uuid !== undefined
      )
      const modifiedProviders = [
        ...validProviders,
        {
          name: <Translate id="articles.my-team-articles" />,
          uuid: 'my-team-articles',
        },
        {
          name: <Translate id="drills.my-favourites" />,
          uuid: 'my-favourites',
        },
      ]
      setProviders(modifiedProviders)
    }
  }, [articleProviders])

  const handleSearchChange = useCallback(
    debounce((newValue) => {
      setSearchQuery(newValue)
      setPage(0)
    }, 400),
    []
  )

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleFavoriteToggle = (article) => {
    updateFavoriteArticle(article.uuid, !article.favorite)
      .then(() => {
        setArticles((prevArticles) =>
          prevArticles.map((a) =>
            a.uuid === article.uuid ? { ...a, favorite: !article.favorite } : a
          )
        )
      })
      .catch((error) => {
        console.error('Error toggling favorite status:', error)
      })
  }

  const handleDeleteArticle = async (article) => {
    setLoading(true)
    try {
      await deleteArticle(article.uuid)
      const filters = {}
      if (selectedSource && selectedSource !== 'my-team-articles') {
        if (selectedCategories.length > 0) {
          filters['filter[categories]'] = selectedCategories.join(',')
        }
      }
      if (searchQuery) {
        filters['filter[search]'] = searchQuery
      }
      await fetchArticles(filters)
    } catch (err) {
      console.error('Error deleting article:', err)
    } finally {
      setLoading(false)
    }
  }

  const openCategoriesDialog = () => {
    setIsDialogOpen(true)
  }

  const closeCategoriesDialog = () => {
    setIsDialogOpen(false)
  }

  return (
    <>
      {isDialogOpen && (
        <CategoriesDialog
          isOpen={isDialogOpen}
          onClose={closeCategoriesDialog}
          categories={articleCategories}
          initialSelectedCategories={selectedCategories}
          onCategoryChange={handleCategoryChange}
        />
      )}
      <Box mb={2} mt={2}>
        <Box className={classes.filtersContainer}>
          <Source
            selectedValue={selectedSource}
            options={providers}
            onChange={handleSourceChange}
            label={<Translate id="drills.source" />}
            labelId="source-label"
          />
          {selectedSource !== 'my-favourites' &&
            selectedSource !== 'my-team-articles' && (
              <>
                <Button
                  variant="contained"
                  onClick={openCategoriesDialog}
                  className={classes.categoriesButton}
                >
                  <Translate id="articles.categories" />
                </Button>
                <Button onClick={handleReset}>
                  <Translate id="button.reset" />
                </Button>
              </>
            )}
        </Box>
        <Box className={classes.searchBarContainer}>
          <Grid container spacing={2} direction="row" alignItems="center">
            <Grid item xs={12} md={6} sm={3}>
              <Translate>
                {({ translate }) => (
                  <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onCancelSearch={() => {
                      setSearchQuery('')
                      handleSearchChange('')
                      setPage(0)
                    }}
                    placeholder={translate('drills.search')}
                    className={classes.searchBar}
                  />
                )}
              </Translate>
            </Grid>
            <Grid item xs={12} md={6} sm={9}>
              <TablePagination
                labelRowsPerPage={<Translate id="drills.rows-perpage" />}
                component="div"
                count={meta?.total ?? 0}
                page={page}
                className={classes.tablePagination}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[12, 24, 36]}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      component: MenuPaper,
                      elevation: 0,
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Grid>
          <Grid container spacing={3}>
            {articles.length > 0 ? (
              articles.map((article) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={article.uuid}>
                  <ArticleCard
                    article={article}
                    onFavoriteToggle={() => handleFavoriteToggle(article)}
                    onDeleteArticle={() => handleDeleteArticle(article)}
                  />
                </Grid>
              ))
            ) : (
              <NotFound />
            )}
          </Grid>
        </Grid>
      )}
    </>
  )
}

ArticlesContainer.propTypes = {
  team: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  deleteArticle: PropTypes.func.isRequired,
}

export default connect(
  ({ articles, team }) => ({
    article: articles.current,
    team,
  }),
  {
    deleteArticle: articlesModule.deleteArticle,
  }
)(ArticlesContainer)
